import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant, CanvasBackgroundTheme } from "./canvasBackgroundTheme";
import { resizeCanvasKeepingPositions } from "./resizeCanvasKeepingPositions";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { BALL_SCALE, BALL_TYPES, type BallType, traceBallBody, traceBallMarks } from "./sportsBallShapes";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

const MOBILE_BALLS_RATIO = 0.5;
const BALLS_COUNT = 7;
const BASE_RADIUS = 18;
const SIZE_MULTIPLIER = 1.7;
const SPEED_MULTIPLIER = 0.4;
const SPIN_MULTIPLIER = 0.4;

export type SportsBallsAnimationProps = {
  hue: number;
  theme: CanvasBackgroundTheme;
  variant?: CanvasAnimationLayoutVariant;
  isMobile?: boolean;
  frameStride?: number;
};

type BallParticle = {
  type: BallType;
  radius: number;
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  gravity: number;
  rotation: number;
  spin: number;
  depth: number;
};

export const SportsBallsAnimation: FC<SportsBallsAnimationProps> = ({
  hue,
  theme,
  variant = "viewportFixed",
  isMobile = false,
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const contextMaybe = canvasMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const canvasEl: HTMLCanvasElement = canvasMaybe;
    const context: CanvasRenderingContext2D = contextMaybe;

    function ballColor(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
        case "light":
          return `hsla(${hue}, 55%, 58%, ${alpha * 0.85})`;
        case "darkdull":
          return `hsla(0, 0%, 82%, ${alpha * 0.78})`;
        case "lightdull":
          return `hsla(0, 0%, 38%, ${alpha * 0.72})`;
        default:
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
      }
    }

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(canvasEl);
      canvasEl.width = width;
      canvasEl.height = height;
    }

    resizeCanvas();

    const ballsCount = isMobile ? Math.round(BALLS_COUNT * MOBILE_BALLS_RATIO) : BALLS_COUNT;
    const balls: BallParticle[] = Array.from({ length: ballsCount }, () =>
      mkBall(canvasEl.width, canvasEl.height, true)
    );

    function onResize(): void {
      resizeCanvasKeepingPositions(canvasEl, balls);
    }

    window.addEventListener("resize", onResize);

    function drawBall(ball: BallParticle, alpha: number): void {
      context.fillStyle = ballColor(alpha * 0.14);
      traceBallBody(context, ball.type, ball.radius);
      context.fill();

      context.strokeStyle = ballColor(alpha);
      context.fillStyle = ballColor(alpha * 0.55);
      context.lineWidth = Math.max(1.8, ball.radius * 0.042);
      traceBallMarks(context, ball.type, ball.radius);

      context.strokeStyle = ballColor(alpha);
      context.lineWidth = Math.max(2.2, ball.radius * 0.05);
      traceBallBody(context, ball.type, ball.radius);
      context.stroke();
    }

    const isDarkDull = theme === "darkdull";

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);

      for (const ball of balls) {
        ball.velocityY += ball.gravity * SPEED_MULTIPLIER * frameSteps;
        ball.x += ball.velocityX * SPEED_MULTIPLIER * frameSteps;
        ball.y += ball.velocityY * SPEED_MULTIPLIER * frameSteps;
        ball.rotation += ball.spin * SPIN_MULTIPLIER * frameSteps;

        const margin = ball.radius * 2;
        if (ball.y - margin > canvasEl.height || ball.x < -margin * 3 || ball.x > canvasEl.width + margin * 3) {
          Object.assign(ball, mkBall(canvasEl.width, canvasEl.height, false));
          continue;
        }

        context.save();
        context.translate(ball.x, ball.y);
        const angle =
          ball.type === "badminton" ? Math.atan2(ball.velocityY, ball.velocityX) - Math.PI / 2 : ball.rotation;
        context.rotate(angle);
        context.lineCap = "round";
        context.lineJoin = "round";
        drawBall(ball, resolveBallAlpha(ball.depth, isDarkDull));
        context.restore();
      }
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [hue, theme, isMobile, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};

function mkBall(width: number, height: number, spawnOnScreen: boolean): BallParticle {
  const type = BALL_TYPES[Math.floor(Math.random() * BALL_TYPES.length)];
  const radius = BASE_RADIUS * BALL_SCALE[type] * SIZE_MULTIPLIER;
  const direction = Math.random() < 0.5 ? 1 : -1;
  return {
    type,
    radius,
    x: spawnOnScreen ? Math.random() * width : direction > 0 ? -radius * 2.5 : width + radius * 2.5,
    y: spawnOnScreen ? Math.random() * height : height * (0.35 + Math.random() * 0.6),
    velocityX: direction * (3.5 + Math.random() * 5),
    velocityY: -(1.6 + Math.random() * 3),
    gravity: 0.014 + Math.random() * 0.024,
    rotation: Math.random() * Math.PI * 2,
    spin: (Math.random() - 0.5) * 0.12,
    depth: 0.6 + Math.random() * 0.4,
  };
}

function resolveBallAlpha(depth: number, isDarkDull: boolean): number {
  const base = 0.34 * depth + 0.1;
  const boosted = isDarkDull ? base * 1.5 + 0.1 : base;
  return Math.min(isDarkDull ? 0.75 : 0.5, boosted);
}
